/**
 * assets/carousel.js — the mobile card carousel behind Convenience and Ingredients.
 *
 * Figma draws both sections as a static four-up row at 1440 with Bullet Navigation
 * present but hidden (277:504, 265:2417), and as a horizontally overflowing row of fixed
 * cards with prev/next and dots at 390 (277:675, 265:2464). No tablet frame exists, so
 * the switch is the theme's one breakpoint, --breakpoint-lg, read from tokens.css at
 * runtime rather than repeated here as a literal.
 *
 * Swiper (assets/swiper.min.mjs, resolved through the import map in layout/theme.liquid)
 * is mounted only below that breakpoint and destroyed at or above it, so the desktop
 * grid is plain CSS and carries no slider state. A carousel that is a carousel at every
 * width — the reviews in Section / Buy, drawn with dots on the desktop frame — says so
 * with data-carousel-always and is never destroyed. Slide spacing is read from the track's
 * own CSS gap, so the section's --space-* token is the single source for it; base.css
 * zeroes that gap while an instance is live.
 *
 * Markup contract, per section:
 *
 *   <div class="carousel" data-carousel data-carousel-nav="ID">
 *     <ul class="carousel__track"><li class="carousel__slide">…</li></ul>
 *   </div>
 *   {% render 'bullet-navigation', id: 'ID' %}
 *
 * bullet-navigation.liquid supplies the prev/next buttons, the dots container and the
 * localised messages (data-label-*) the A11y module announces, so no English lives here.
 *
 * AUTOPLAY is opt-in per carousel, `data-carousel-autoplay="<ms>"`, and the reviews in
 * Section / Buy are the only consumer. There is no motion token for a slide's dwell time
 * — the export's --motion-duration-* are transition lengths, orders of magnitude too
 * short — so the number is a merchant setting rather than a literal here
 * (figma-changes §7.84).
 *
 * It never runs under `prefers-reduced-motion`, and the preference is watched, not read
 * once: a visitor who turns it on mid-session gets the motion stopped. WCAG 2.2.2 wants
 * auto-moving content to be stoppable, so three things stop it — a pointer entering the
 * carousel, keyboard focus reaching anything inside it, and any use of the dots or
 * arrows, which stops it for good rather than pausing.
 */
/*
 * SWIPER LOADS AFTER THE PAGE, NOT WITH IT (PageSpeed, 2026-10-29). Every carousel paints
 * its first slide with plain CSS, so nothing here is needed for the first screen — but a
 * static import fetched and ran Swiper beside the buy box's LCP image. It is a dynamic
 * import now, started once the window has loaded and the main thread is idle. Anything a
 * shopper does to a carousel before then — a touch, a focus, an arrow — mounts that one
 * at once, and an arrow pressed before the mount is replayed after it, so the first swipe
 * is never dead.
 *
 * Images a slide holds back (data-srcset, see snippets/product-gallery.liquid) are given
 * their real sources when the carousel mounts: until then nobody can reach them.
 */

let swiperModule = null;
const loadSwiper = () => (swiperModule ??= import('swiper'));

const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
const mounted = new WeakMap();
const mounting = new WeakMap(); // el -> the mount in flight, so a second caller can await it
let desktop = null; // matchMedia for --breakpoint-lg, read once the page is idle

function hydrateImages(el) {
  for (const img of el.querySelectorAll('img[data-srcset]')) {
    img.srcset = img.dataset.srcset;
    img.src = img.dataset.src;
    img.removeAttribute('data-srcset');
  }
}

function mount(el) {
  if (mounted.has(el)) return Promise.resolve(mounted.get(el));
  if (!mounting.has(el)) {
    mounting.set(
      el,
      loadSwiper()
        .then((swiper) => build(el, swiper))
        .finally(() => mounting.delete(el))
    );
  }
  return mounting.get(el);
}

function build(el, { default: Swiper, A11y, Autoplay, Keyboard, Navigation, Pagination }) {
  if (mounted.has(el) || !el.isConnected) return mounted.get(el);
  if (desktop && !wanted(el)) return null; // the viewport crossed the breakpoint while Swiper loaded
  const track = el.querySelector('.carousel__track');
  if (!track) return null;
  hydrateImages(el);

  // Controls live either in a bullet-navigation named by data-carousel-nav, or inside the
  // carousel itself — the buy box gallery keeps its arrows over the image, so there is no
  // separate nav element to point at.
  const nav = el.dataset.carouselNav ? document.getElementById(el.dataset.carouselNav) : el;
  const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
  // A dots-only nav (the buy gallery on mobile) leaves the arrows where they were, over
  // the image — so they come from the nav when it has them, else from the carousel.
  const arrows = nav?.querySelector('[data-carousel-prev]') ? nav : el.querySelector('[data-carousel-prev]') ? el : null;

  // Only messages the snippet actually supplied; an undefined key would replace
  // Swiper's default with nothing.
  const a11y = {};
  if (nav) {
    for (const [attr, key] of [
      ['labelPrev', 'prevSlideMessage'],
      ['labelNext', 'nextSlideMessage'],
      ['labelSlide', 'paginationBulletMessage'],
      ['labelOf', 'slideLabelMessage'],
    ]) {
      if (nav.dataset[attr]) a11y[key] = nav.dataset[attr];
    }
  }
  // A list track keeps its list semantics. Swiper's default slide role is `group`, which
  // left every <ul role="list"> with no listitems (Lighthouse, 2026-10-29). A <div>
  // track — the gallery — keeps the default.
  if (track.tagName === 'UL') a11y.slideRole = 'listitem';

  // Opt-in, and only when motion is welcome. `disableOnInteraction` is the WCAG 2.2.2
  // stop: once a shopper touches the controls it stays stopped.
  const delay = Number(el.dataset.carouselAutoplay) || 0;
  const autoplay =
    delay > 0 && !reduced.matches
      ? { delay, disableOnInteraction: true, pauseOnMouseEnter: true }
      : false;

  const swiper = new Swiper(el, {
    modules: [A11y, Autoplay, Keyboard, Navigation, Pagination],
    autoplay,
    wrapperClass: 'carousel__track',
    slideClass: 'carousel__slide',
    slidesPerView: 'auto',
    spaceBetween: gap,
    watchOverflow: true,
    keyboard: { enabled: true, onlyInViewport: true },
    a11y,
    navigation: arrows
      ? {
          prevEl: arrows.querySelector('[data-carousel-prev]'),
          nextEl: arrows.querySelector('[data-carousel-next]'),
          disabledClass: 'is-disabled',
          lockClass: 'is-locked',
        }
      : false,
    pagination: nav?.querySelector('[data-carousel-pagination]')
      ? {
          el: nav.querySelector('[data-carousel-pagination]'),
          clickable: true,
          bulletElement: 'button',
          bulletClass: 'bullet-navigation__dot',
          bulletActiveClass: 'bullet-navigation__dot--active',
          lockClass: 'is-locked',
        }
      : false,

    // A DOM event rather than a handle on the instance, so a section can follow the
    // carousel without reaching into this module or racing its mount — assets/buy.js
    // loads before this file and would find el.swiper undefined.
    on: {
      slideChange: (swiper) => {
        el.dispatchEvent(
          new CustomEvent('carousel:change', {
            bubbles: true,
            detail: { index: swiper.activeIndex },
          })
        );
      },
    },
  });

  // pauseOnMouseEnter covers pointers only. A keyboard shopper reading a review should
  // not have it slide away under them either.
  //
  // `enabled = false` before stop() is the load-bearing half: stop() on its own is
  // undone within a few hundred ms — Swiper restarts autoplay off its own slide and
  // transition events — and measured that way the slide still advanced past a focused
  // card. Clearing the flag makes the stop final, which is what WCAG 2.2.2 asks for.
  if (autoplay) {
    el.addEventListener('focusin', () => {
      if (!swiper.autoplay) return;
      swiper.params.autoplay.enabled = false;
      swiper.autoplay.stop();
    });
  }

  mounted.set(el, swiper);

  // Followers (the gallery's thumbnails, the Flavors can spin) sync on carousel:change;
  // a carousel that mounts after they looked needs to tell them where it starts.
  el.dispatchEvent(new CustomEvent('carousel:change', { bubbles: true, detail: { index: swiper.activeIndex } }));
  return swiper;
}

function wanted(el) {
  return !(desktop?.matches ?? matchMedia(`(min-width: ${breakpointLg()})`).matches) || el.hasAttribute('data-carousel-always');
}

function breakpointLg() {
  return getComputedStyle(document.documentElement).getPropertyValue('--breakpoint-lg').trim();
}

// Before the idle mount: the first interaction with a carousel mounts it on the spot.
// An arrow pressed in that window is replayed once Swiper is there.
function armEarly(el) {
  if (el.dataset.carouselArmed) return;
  el.dataset.carouselArmed = '';
  const early = (event) => {
    if (mounted.has(el) || !wanted(el)) return;
    const arrow = event.type === 'click' ? event.target.closest?.('[data-carousel-prev], [data-carousel-next]') : null;
    mount(el).then((swiper) => {
      if (!swiper || !arrow) return;
      if (arrow.hasAttribute('data-carousel-next')) swiper.slideNext();
      else swiper.slidePrev();
    });
  };
  for (const type of ['pointerdown', 'touchstart', 'focusin', 'click']) {
    el.addEventListener(type, early, { passive: true, capture: true });
  }
  // A separate nav (bullet-navigation) holds the arrows for Convenience and the rest.
  const nav = el.dataset.carouselNav && document.getElementById(el.dataset.carouselNav);
  if (nav && nav !== el) nav.addEventListener('click', early, { passive: true, capture: true });
}

function unmount(el) {
  const swiper = mounted.get(el);
  if (!swiper) return;
  swiper.destroy(true, true);
  mounted.delete(el);
}

function sync(scope = document) {
  for (const el of scope.querySelectorAll('[data-carousel]')) {
    (wanted(el) ? mount : unmount)(el);
  }
}

function start() {
  desktop = window.matchMedia(`(min-width: ${breakpointLg()})`);
  desktop.addEventListener('change', () => sync());

  // Motion preference is a live setting, not a page-load fact. Remount so autoplay is
  // rebuilt on the new answer — Swiper cannot switch a disabled autoplay back on.
  reduced.addEventListener('change', () => {
    for (const el of document.querySelectorAll('[data-carousel]')) unmount(el);
    sync();
  });

  sync();
}

// Theme editor: sections re-render in place.
document.addEventListener('shopify:section:load', (event) => {
  for (const el of event.target.querySelectorAll('[data-carousel]')) armEarly(el);
  if (desktop) sync(event.target);
});
document.addEventListener('shopify:section:unload', (event) => {
  for (const el of event.target.querySelectorAll('[data-carousel]')) unmount(el);
});

for (const el of document.querySelectorAll('[data-carousel]')) armEarly(el);

// After load, then idle. requestIdleCallback is missing in Safari and iOS WebViews
// (Meta's in-app browser on iPhone among them), where a short timeout stands in.
const idle = (fn) => ('requestIdleCallback' in window ? requestIdleCallback(fn, { timeout: 2000 }) : setTimeout(fn, 200));
if (document.readyState === 'complete') idle(start);
else window.addEventListener('load', () => idle(start), { once: true });
